import { useEffect, useMemo } from 'react';
import { useParams } from './useRouteParams';
import {
  getAssignmentDetails,
  getQuestionFromQuestionId,
  getReviewAnswerStatus,
  getReviewQuestionUrl
} from '../utils';
import { REVIEW_STATUS, QUESTION_RESPONSE_STATUS } from '../constants';
import { useNavigate } from 'react-router-dom';

const useAssignmentReview = ({ assignmentData }) => {
  const { questionId, studentAssignmentId } = useParams();
  const navigate = useNavigate();
  const {
    name,
    nextQuestionId,
    isLastQuestion,
    currentQuestion,
    isFirstQuestion,
    previousQuestionId,
    currentQuestionIndex,
    currentQuestionResponse,
    answerScoreData
  } = useMemo(
    () =>
      getAssignmentDetails({
        assignmentData,
        questionId
      }),
    [assignmentData, questionId]
  );

  const firstQuestionId = assignmentData?.studentResponse?.[0]?.questionId;

  useEffect(() => {
    if (!assignmentData || !firstQuestionId) {
      return;
    }

    if (!questionId || !currentQuestion) {
      navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: firstQuestionId }));
    }
  }, [assignmentData, questionId, currentQuestion, firstQuestionId, navigate, studentAssignmentId]);

  if (!assignmentData) {
    return {};
  }

  if (!questionId || !currentQuestion) {
    return { isRedirecting: Boolean(firstQuestionId) };
  }

  const scoreMap = assignmentData.result?.scoreMap;

  const drawerData = assignmentData.studentResponse.map((questionResponse) => {
    const { questionId, status } = questionResponse;
    const answerScore = scoreMap?.[questionId];
    const data = {
      questionId,
      status: REVIEW_STATUS.SKIPPED,
      // Whether this answer was one of the ones a choice group paid for. The tray reports
      // how many of a group counted, which is the number that decided the student's marks -
      // not how many they attempted, since answering all of them earns no more.
      countedInChoiceGroup: Boolean(answerScore?.countedInChoiceGroup)
    };
    if (status === QUESTION_RESPONSE_STATUS.ANSWERED) {
      const question = getQuestionFromQuestionId(assignmentData.assignment, questionId);
      data.status = getReviewAnswerStatus(question, questionResponse, answerScore);
    }
    return data;
  });

  if (currentQuestion.options) {
    currentQuestion.options = currentQuestion.options.filter(
      (option) => option.id === currentQuestionResponse?.answer?.[0] || option.isCorrect
    );
  }

  const onNext = () => {
    navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: nextQuestionId }));
  };

  const onPrevious = () => {
    navigate(getReviewQuestionUrl({ studentAssignmentId, questionId: previousQuestionId }));
  };

  const answerStatus = getReviewAnswerStatus(
    currentQuestion,
    currentQuestionResponse,
    answerScoreData
  );

  return {
    currentQuestion,
    answerStatus,
    currentQuestionIndex,
    currentQuestionResponse,
    currentAnswerScoreData: answerScoreData,
    onNext,
    onPrevious,
    name,
    drawerData,
    isFirstQuestion,
    isLastQuestion,
    totalQuestions: assignmentData?.studentResponse?.length ?? 0
  };
};

export { useAssignmentReview };
